import { AllAgenciesDto } from '@zorro/clients';
import { MultiSelect } from '@zorro/design-system/v2/MultiSelect';
import { NumberInput } from '@zorro/design-system/v2/NumberInput';
import { Select } from '@zorro/design-system/v2/Select';
import { TagsInput } from '@zorro/design-system/v2/TagsInput';
import { TextInput } from '@zorro/design-system/v2/TextInput';
import { Textarea } from '@zorro/design-system/v2/Textarea';
import { EN_LOCALE_DATE_FORMAT } from '@zorro/shared/formatters';
import {
  FormErrorMessage,
  Grid,
  LabelWithTooltip,
  MonthPickerInput,
} from '@zorro/web/design-system';
import { useMonolithQuery, useRoles } from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import {
  ENROLLMENT_TEAM_RESPONSIBILITIES_MAX_LENGTH,
  ZorroSetupFormFields,
  getMonthlySEPDeadlineOptions,
} from './ZorroSetupUtils';

type Props = {
  zorroSetupForm: UseFormReturn<ZorroSetupFormFields>;
  allAgencies: AllAgenciesDto;
  isFinalizationMode?: boolean;
  isProspectMode?: boolean;
};

export const ZorroSetupForm = ({
  zorroSetupForm,
  allAgencies,
  isFinalizationMode,
  isProspectMode = false,
}: Props) => {
  const {
    isAgent,
    isAgentWithoutSupervisor: isReadonlyMode,
    isZorroOperations,
    isLoading: isRolesLoading,
  } = useRoles();

  const { zorroPartners, enrollmentTeams, producers } = allAgencies;
  const { control, formState, watch } = zorroSetupForm;
  const { errors } = formState;

  const producerId = watch('producerId');
  const enrollmentTeamIds = watch('enrollmentTeamIds');
  const { data: producerAgents = [] } = useMonolithQuery({
    method: 'agentsControllerFindAllForAgency',
    params: producerId && [producerId],
  });

  const { data: omnipotentAdmins = [] } = useMonolithQuery({
    method: 'usersControllerGetOmnipotentAdmins',
    params: [],
    enabled: isZorroOperations,
  });

  const enrollmentTeamData = useMemo(() => {
    const agencies = [...enrollmentTeams, ...zorroPartners];
    return agencies.map((agency) => ({
      label: agency.name,
      value: agency.id,
    }));
  }, [enrollmentTeams, zorroPartners]);

  return (
    <Grid>
      <Grid.Col span={isZorroOperations ? 6 : 12}>
        <Controller
          control={control}
          name="zorroPartnerId"
          render={({ field }) => (
            <Select
              {...field}
              placeholder="Select a partner"
              data={zorroPartners.map((zorroPartner) => ({
                label: zorroPartner.name,
                value: zorroPartner.id,
              }))}
              disabled={isRolesLoading}
              isReadOnly={isReadonlyMode || isAgent}
              size="md"
              searchable
              // TODO: check how to fix this
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              scrollAreaProps={{ 'data-test-id': 'zorro-partner' }}
              label={
                <LabelWithTooltip
                  label="Zorro Partner"
                  tooltipLabel="Entity responsible for ICHRA/CHOICE plan design and set up"
                  isRequired
                />
              }
            />
          )}
        />

        <FormErrorMessage fieldName="zorroPartnerId" errors={errors} />
      </Grid.Col>

      {isZorroOperations && (
        <Grid.Col span={6}>
          <Controller
            name="csmUserId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                data={omnipotentAdmins.map((user) => ({
                  label: `${user.firstName} ${user.lastName} (${user.email})`,
                  value: user.id,
                }))}
                searchable
                clearable
                disabled={isRolesLoading}
                placeholder="Select a CSM"
                size="md"
                label={
                  <LabelWithTooltip
                    label="CSM"
                    tooltipLabel="Zorro client success manager who owns this employer."
                  />
                }
              />
            )}
          />
          <FormErrorMessage fieldName="csmUserId" errors={errors} />
        </Grid.Col>
      )}

      <Grid.Col>
        <Controller
          name="enrollmentTeamIds"
          control={control}
          render={({ field: { value, onChange, ...rest } }) => (
            <MultiSelect
              {...rest}
              data={enrollmentTeamData}
              value={value}
              onChange={onChange}
              placeholder="Select enrollment team(s)"
              isReadOnly={isReadonlyMode}
              size="md"
              label={
                <LabelWithTooltip
                  label="Enrollment team(s)"
                  tooltipLabel="Entity responsible for enrolling employees with carriers"
                  isRequired={isFinalizationMode}
                />
              }
            />
          )}
        />
        <FormErrorMessage fieldName="enrollmentTeamIds" errors={errors} />
      </Grid.Col>

      {isZorroOperations && enrollmentTeamIds?.length > 1 && (
        <Grid.Col>
          <Controller
            name="enrollmentTeamResponsibilities"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                label="Enrollment team responsibilities"
                placeholder="Document which enrollment team handles what"
                maxLength={ENROLLMENT_TEAM_RESPONSIBILITIES_MAX_LENGTH}
                autosize
                minRows={2}
                maxRows={2}
                isReadOnly={isReadonlyMode}
              />
            )}
          />
          <FormErrorMessage
            fieldName="enrollmentTeamResponsibilities"
            errors={errors}
          />
        </Grid.Col>
      )}

      <Grid.Col span={6}>
        <Controller
          name="producerId"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              data={[...producers, ...zorroPartners].map((agency) => ({
                label: agency.name,
                value: agency.id,
              }))}
              searchable
              isReadOnly={isReadonlyMode}
              disabled={isRolesLoading}
              placeholder="Select a producer agency"
              size="md"
              label={
                <LabelWithTooltip
                  label="Producer agency"
                  tooltipLabel="Entity responsible for managing the employer's benefits"
                  isRequired={isFinalizationMode}
                />
              }
            />
          )}
        />
        <FormErrorMessage fieldName="producerId" errors={errors} />
      </Grid.Col>

      <Grid.Col span={6}>
        <Controller
          name="producerAgentId"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              data={producerAgents.map((agent) => ({
                label: `${agent.firstName} ${agent.lastName} (${agent.email})`,
                value: agent.id,
              }))}
              searchable
              disabled={!producerId || isRolesLoading}
              isReadOnly={isReadonlyMode}
              placeholder="Select a producer agent"
              size="md"
              label={
                <LabelWithTooltip
                  label="Producer agent"
                  tooltipLabel="Individual within the producer agency who is the primary employer's Point Of Contact"
                />
              }
            />
          )}
        />
        <FormErrorMessage fieldName="producerAgentId" errors={errors} />
      </Grid.Col>

      {isZorroOperations && !isProspectMode && (
        <>
          <Grid.Col>
            <Controller
              name="ccEmailList"
              control={control}
              render={({ field: { onBlur, onChange, ...rest } }) => (
                <TagsInput
                  {...rest}
                  onBlur={onBlur}
                  onChange={(value) => {
                    onChange(value);
                    onBlur();
                  }}
                  disabled={isRolesLoading}
                  isReadOnly={isReadonlyMode}
                  placeholder="Type CC email list"
                  label="CC email list"
                  size="md"
                />
              )}
            />
            <FormErrorMessage fieldName="ccEmailList" errors={errors} />
          </Grid.Col>

          <Grid.Col>
            <Controller
              control={control}
              name="consultationLink"
              render={({ field: { ...rest } }) => (
                <TextInput
                  {...rest}
                  label="Consultation link"
                  placeholder="Consultation link"
                  size="md"
                />
              )}
            />

            <FormErrorMessage fieldName="consultationLink" errors={errors} />
          </Grid.Col>
        </>
      )}

      <Grid.Col span={6}>
        <Controller
          name="crmRecordId"
          control={control}
          render={({ field: { value, ...rest } }) => (
            <NumberInput
              {...rest}
              value={value || ''}
              placeholder="Type CRM record ID"
              disabled={isRolesLoading}
              isReadOnly={isReadonlyMode}
              size="md"
              label={
                <LabelWithTooltip
                  label="CRM record ID"
                  tooltipLabel="Unique identifier in the CRM system for connecting customer records across platforms"
                />
              }
            />
          )}
        />
        <FormErrorMessage fieldName="crmRecordId" errors={errors} />
      </Grid.Col>

      {!isProspectMode && (
        <Grid.Col span={6}>
          <Controller
            name="monthlySEPDeadline"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                data={getMonthlySEPDeadlineOptions()}
                isReadOnly={isReadonlyMode}
                placeholder="Select the monthly SEP deadline"
                size="md"
                label={
                  <LabelWithTooltip
                    label="Monthly SEP deadline"
                    tooltipLabel="Sets the default deadline for special enrollment period elections"
                    isRequired
                  />
                }
              />
            )}
          />
          <FormErrorMessage fieldName="monthlySEPDeadline" errors={errors} />
        </Grid.Col>
      )}
      {isProspectMode && (
        <Grid.Col span={6}>
          <Controller
            control={control}
            name="prospectCoverageStartDate"
            render={({ field: { ...rest } }) => (
              <MonthPickerInput
                {...rest}
                disabled={isReadonlyMode || isRolesLoading}
                label="Coverage start date"
                placeholder="Coverage start date"
                valueFormat={EN_LOCALE_DATE_FORMAT}
                size="md"
                required
              />
            )}
          />
        </Grid.Col>
      )}

      <Grid.Col span={6}>
        <Controller
          name="estimatedEmployees"
          control={control}
          render={({ field: { value, ...rest } }) => (
            <NumberInput
              {...rest}
              value={value ?? undefined}
              required={isFinalizationMode}
              placeholder="Enter total employees"
              isReadOnly={isReadonlyMode}
              size="md"
              label="Estimated employees"
            />
          )}
        />
        <FormErrorMessage fieldName="estimatedEmployees" errors={errors} />
      </Grid.Col>

      <Grid.Col span={6}>
        <Controller
          control={control}
          name="estimatedParticipationRate"
          render={({ field: { value, ...rest } }) => (
            <NumberInput
              {...rest}
              value={value ?? undefined}
              required={isFinalizationMode}
              suffix="%"
              placeholder="Enter percent enrolled"
              isReadOnly={isReadonlyMode}
              size="md"
              label="Estimated participation rate (%)"
            />
          )}
        />
        <FormErrorMessage
          fieldName="estimatedParticipationRate"
          errors={errors}
        />
      </Grid.Col>
    </Grid>
  );
};
